import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Live authenticator (TOTP) code with a client-side countdown.
 *
 * We poll the backend only at rollover (when secondsRemaining reaches 0),
 * NOT every second, to respect the rate limit — the digits are stable for
 * the whole period, so we tick down locally and re-fetch once the window
 * rolls over. `fetchCode()` resolves to
 * `{ method, code, secondsRemaining, period, digits }`.
 */
export default function LiveCode({ fetchCode }) {
  const [state, setState] = useState({ method: null, code: null, remaining: null, period: 30 });
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetchCode();
      setState({
        method: r.method,
        code: r.code,
        remaining: typeof r.secondsRemaining === 'number' ? r.secondsRemaining : null,
        period: r.period || 30,
      });
    } catch {
      setState((s) => ({ ...s, code: null, method: 'error' }));
    }
  }, [fetchCode]);

  useEffect(() => {
    refresh();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [refresh]);

  // Tick every second; re-fetch at rollover.
  useEffect(() => {
    if (state.remaining === null) return undefined;
    timerRef.current = setTimeout(() => {
      setState((s) => {
        const next = (s.remaining ?? 1) - 1;
        if (next <= 0) { refresh(); return s; }
        return { ...s, remaining: next };
      });
    }, 1000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [state.remaining, refresh]);

  function copy() {
    if (!state.code) return;
    try {
      navigator.clipboard.writeText(state.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the code is still visible to type manually */ }
  }

  if (state.method === 'unknown' || (state.method === null && !state.code)) {
    return <div className="badge-livecode badge-livecode--wait">Getting your code ready, hang tight…</div>;
  }
  if (state.method === 'error') {
    return (
      <div className="badge-livecode badge-livecode--wait">
        Couldn&apos;t load the code, <button type="button" className="badge-linkbtn" onClick={refresh}>try again</button>.
      </div>
    );
  }

  const pct = state.remaining !== null && state.period
    ? Math.max(0, Math.min(100, (state.remaining / state.period) * 100))
    : 100;

  return (
    <div className="badge-livecode">
      <button type="button" className="badge-code" onClick={copy} title="Tap to copy">
        {state.code || '••••••'}
      </button>
      <div className="badge-code-copy">{copied ? 'Copied!' : 'Tap the code to copy'}</div>
      {state.remaining !== null && (
        <div className="badge-countdown" aria-label={`${state.remaining} seconds remaining`}>
          <div className="badge-countdown-bar" style={{ width: `${pct}%` }} />
          <span className="badge-countdown-text">Refreshes in {state.remaining}s</span>
        </div>
      )}
    </div>
  );
}
